/* Live eBay auctions — product panel + auction index */

(function () {
  'use strict';

  var REFRESH_MS = 60000;
  var TICK_MS = 15000;

  function money(value, currency) {
    var amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      }).format(amount);
    } catch (e) {
      return '£' + amount.toFixed(2);
    }
  }

  function safeUrl(value) {
    if (!value) return '';
    return /^https?:\/\//i.test(value) ? value : '';
  }

  // "Yu-Gi-Oh!" -> "yugioh", "Pokémon" -> "pokemon". Keeps ?vendor= links in the
  // navigation readable and tolerant of punctuation, accents and casing.
  function vendorSlug(value) {
    var text = String(value == null ? '' : value).toLowerCase();
    if (text.normalize) text = text.normalize('NFD');
    return text.replace(/[^a-z0-9]+/g, '');
  }

  // The API returns scheduled lots alongside live ones. state is "live" or
  // "upcoming"; startsAt is only meaningful for the latter and can be null.
  function isUpcoming(row) {
    return !!row && row.state === 'upcoming';
  }

  // Deliberately a fixed date, never a countdown: a ticking clock on something
  // that has not started reads as though it is about to end.
  function openingText(row) {
    var when = row.startsAt ? new Date(row.startsAt) : null;

    // No start time published yet. It lists within minutes, so promise nothing
    // more precise than that.
    if (!when || isNaN(when.getTime())) return 'Going live shortly';

    var locale = document.documentElement.lang || 'en-GB';
    var formatted;
    try {
      formatted = new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit'
      }).format(when);
    } catch (e) {
      formatted = when.toLocaleString();
    }
    return 'Bidding opens ' + formatted;
  }

  // Live lots sort above upcoming ones everywhere: they are the only lots a
  // visitor can act on right now.
  function statePriority(row) {
    return isUpcoming(row) ? 1 : 0;
  }

  // Within a group: live lots by the one closing soonest, upcoming lots by the
  // one opening soonest.
  function stateSortKey(row) {
    var iso = isUpcoming(row) ? row.startsAt || row.endsAt : row.endsAt;
    var time = new Date(iso).getTime();
    return isNaN(time) ? Infinity : time;
  }

  function byStateThenTime(a, b) {
    var byState = statePriority(a) - statePriority(b);
    return byState !== 0 ? byState : stateSortKey(a) - stateSortKey(b);
  }

  // "7 auctions live" once meant every row. Now that scheduled lots are in the
  // same list, say which is which rather than calling an unopened lot live.
  function countText(rows) {
    var upcoming = rows.filter(isUpcoming).length;
    var live = rows.length - upcoming;

    if (!upcoming) return live === 1 ? '1 auction live' : live + ' auctions live';
    if (!live) return upcoming === 1 ? '1 auction upcoming' : upcoming + ' auctions upcoming';
    return live + ' live · ' + upcoming + ' upcoming';
  }

  function unitLabel(value, singular, plural) {
    return value + ' ' + (value === 1 ? singular : plural);
  }

  function msUntil(iso) {
    var ends = new Date(iso).getTime();
    return isNaN(ends) ? NaN : ends - Date.now();
  }

  function countdownData(ms, withSuffix) {
    if (ms <= 0) return { type: 'text', text: 'Ending now' };

    var mins = Math.floor(ms / 60000);
    var days = Math.floor(mins / 1440);
    var hours = Math.floor((mins % 1440) / 60);
    var remainMins = mins % 60;
    var parts = [];

    if (days > 0) parts.push(unitLabel(days, 'day', 'days'));
    if (hours > 0) parts.push(unitLabel(hours, 'hour', 'hours'));
    if (remainMins > 0 && days === 0) parts.push(unitLabel(remainMins, 'minute', 'minutes'));

    if (!parts.length) {
      return { type: 'text', text: withSuffix ? 'Under a minute left' : 'Under a minute' };
    }
    return { type: 'parts', parts: parts, suffix: withSuffix ? 'left' : null };
  }

  function appendCountdown(node, ms, withSuffix) {
    var data = countdownData(ms, withSuffix);
    if (data.type === 'text') {
      node.appendChild(document.createTextNode(data.text));
      return;
    }
    data.parts.forEach(function (part) {
      node.appendChild(el('span', null, part));
    });
    if (data.suffix) node.appendChild(el('span', null, data.suffix));
  }

  function fillCountdown(node, ms, withSuffix) {
    node.replaceChildren();
    if (isNaN(ms)) return;
    appendCountdown(node, ms, withSuffix);
  }

  function timeLeft(iso) {
    var ms = msUntil(iso);
    if (isNaN(ms)) return { urgent: false, ended: false, ms: ms };

    var mins = Math.floor(ms / 60000);
    var days = Math.floor(mins / 1440);
    var hours = Math.floor((mins % 1440) / 60);

    return {
      urgent: ms <= 0 || (days === 0 && (hours === 0 || hours < 2)),
      ended: ms <= 0,
      ms: ms
    };
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function icon(name) {
    var i = document.createElement('i');
    i.className = name;
    i.setAttribute('aria-hidden', 'true');
    return i;
  }

  function timeBadge(row) {
    var base = 'badge fs-6 fw-semibold px-3 py-2 d-inline-flex align-items-center gap-2';

    if (isUpcoming(row)) {
      var upcoming = el('span', base + ' tc-auction-upcoming-badge');
      upcoming.appendChild(icon('bi bi-calendar-event'));
      upcoming.appendChild(el('span', null, openingText(row)));
      return upcoming;
    }

    var left = timeLeft(row.endsAt);
    var classes = base;
    classes += left.urgent ? ' bg-danger-subtle text-danger border border-danger-subtle' : ' bg-light text-dark border';
    var badge = el('span', classes);
    badge.appendChild(icon('bi bi-clock'));
    appendCountdown(badge, left.ms, true);
    return badge;
  }

  function priceParts(row, currency) {
    // Nothing has been bid on a lot that has not opened, so there is no bid
    // count to show and the figure is the opening bid, not a current one.
    if (isUpcoming(row)) {
      return {
        label: 'Opening bid',
        amount: money(row.startPrice, currency),
        bids: null
      };
    }

    var hasBids = Number(row.bidCount) > 0;
    return {
      label: hasBids ? 'Current bid' : 'Start price',
      amount: money(hasBids ? row.currentBid : row.startPrice, currency),
      bids: hasBids
        ? row.bidCount + (Number(row.bidCount) === 1 ? ' bid' : ' bids')
        : 'No bids yet'
    };
  }

  // Set by hand in the admin panel: eBay's Trading API exposes nothing that
  // identifies a lot as promoted in a livestream, so a tagged URL is the only
  // signal there is. Run through safeUrl like every other API-supplied href.
  function streamUrl(row) {
    return safeUrl(row && row.ebayLiveUrl);
  }

  // A lot running in an eBay Live stream. Its currentBid and endsAt still come
  // back from the API, but bidding has moved into the stream, so neither figure
  // describes reality any more — both are suppressed wherever this is true.
  function isStreamLot(row) {
    return !!streamUrl(row);
  }

  function linkButton(href, label, iconName, className) {
    var button = el('a', 'tc-auction-btn d-inline-flex align-items-center justify-content-center text-nowrap rounded-1 lh-sm ' + className);
    button.href = href;
    button.target = '_blank';
    button.rel = 'noopener nofollow';
    if (iconName) button.appendChild(icon(iconName + ' me-2'));
    button.appendChild(el('span', null, label));
    return button;
  }

  // The only two actions that mean anything on a stream lot: see the listing,
  // or go and watch. Ordered with the stream first, since that is where the
  // bidding actually happens.
  function streamActions(row, extraClass) {
    var actions = el('div', 'tc-auction-actions d-flex flex-wrap gap-2' + (extraClass ? ' ' + extraClass : ''));

    actions.appendChild(linkButton(streamUrl(row), 'Watch livestream', 'bi bi-broadcast', 'tc-auction-btn--stream text-white'));

    var listing = safeUrl(row.ebayUrl);
    if (listing) {
      actions.appendChild(linkButton(listing, 'View listing', 'bi bi-box-arrow-up-right', 'tc-auction-btn--listing'));
    }
    return actions;
  }

  function ctaButton(row, extraClass) {
    // ebayUrl is null until the listing exists. A dead or missing link is worse
    // than no link, so upcoming lots get no button at all.
    if (isUpcoming(row)) return null;

    var href = safeUrl(row.ebayUrl);
    if (!href) return null;

    var cta = el('a', 'd-inline-flex align-items-center tc-button-teal text-white rounded-1 lh-sm px-3 py-2 fs-5 text-nowrap' + (extraClass ? ' ' + extraClass : ''));
    cta.href = href;
    cta.target = '_blank';
    cta.rel = 'noopener nofollow';
    cta.appendChild(document.createTextNode('Bid on eBay'));
    cta.appendChild(icon('bi bi-box-arrow-up-right ms-2 fs-7'));
    return cta;
  }

  function request(endpoint, query) {
    var url = endpoint + (endpoint.indexOf('?') === -1 ? '?' : '&') + query;
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Auction API responded ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        return (payload && payload.data) || {};
      });
  }

  function poll(fn, interval) {
    var timer = null;

    function start() {
      if (timer) return;
      timer = setInterval(fn, interval);
    }

    function stop() {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
      } else {
        fn();
        start();
      }
    });

    if (!document.hidden) start();
  }

  /* Product page panel */

  function auctionRow(row, currency) {
    var sku = row.auctionSku || row.parentSku;

    // In a stream, the bid and the end time are both meaningless, so the row
    // carries neither — just what it is and the two places to go.
    if (isStreamLot(row)) {
      var streamItem = el('li', 'border-top py-3 px-3 tc-auction-row--stream');
      var streamInfo = el('div', 'mb-2');
      streamInfo.appendChild(el('div', 'fs-7 text-muted text-uppercase', 'eBay Live'));
      streamInfo.appendChild(el('div', 'fw-bold tc-text-blue fs-6 lh-sm', 'Bidding happens in the livestream'));
      if (sku) streamInfo.appendChild(el('div', 'fs-7 text-muted', sku));
      streamItem.appendChild(streamInfo);
      streamItem.appendChild(streamActions(row));
      return streamItem;
    }

    var price = priceParts(row, currency);
    var classes = 'border-top py-3 d-flex flex-wrap align-items-center gap-3';
    if (isUpcoming(row)) classes += ' tc-auction-row--upcoming px-3';
    var item = el('li', classes);

    var figures = el('div', 'flex-grow-1');
    figures.appendChild(el('div', 'fs-7 text-muted text-uppercase', price.label));
    figures.appendChild(el('div', 'fw-bold tc-text-blue fs-5 lh-sm', price.amount));
    if (price.bids) figures.appendChild(el('div', 'fs-7 text-muted', price.bids));

    var timing = el('div', 'd-flex flex-column align-items-start gap-1');
    timing.appendChild(timeBadge(row));
    if (sku) timing.appendChild(el('span', 'fs-7 text-muted', sku));

    item.appendChild(figures);
    item.appendChild(timing);

    var cta = ctaButton(row, 'ms-sm-auto w-100 w-sm-auto');
    if (cta) item.appendChild(cta);

    return item;
  }

  function initPanel(root) {
    var endpoint = root.dataset.endpoint;
    var productId = root.dataset.productId;
    if (!endpoint || !productId) return;

    var currency = root.dataset.currency;
    var list = root.querySelector('[data-auction-list]');
    var count = root.querySelector('[data-auction-count]');
    var rows = [];

    function render() {
      // Same "Live on eBay" ribbon problem as the grid: on a product whose only
      // auctions are scheduled, the image badge must not claim it is live.
      // Scoped to this product's own section and skipping .product-box, because
      // product-badge.liquid also renders that ribbon on every related-product
      // card on the page — those belong to other products entirely.
      var anyLive = rows.some(function (row) { return !isUpcoming(row); });
      var section = root.closest('.shopify-section') || document;
      section.querySelectorAll('.badge-live-auction').forEach(function (ribbon) {
        if (ribbon.closest('.product-box')) return;
        ribbon.textContent = anyLive ? 'Live on eBay' : 'Auction soon';
        ribbon.classList.toggle('badge-upcoming-auction', !anyLive);
      });

      list.textContent = '';
      rows
        .slice()
        .sort(byStateThenTime)
        .forEach(function (row) {
          list.appendChild(auctionRow(row, currency));
        });

      if (count) {
        count.textContent = rows.length === 1 ? '1 auction' : rows.length + ' auctions';
        count.hidden = false;
      }
    }

    function load() {
      return request(endpoint, 'productId=' + encodeURIComponent(productId))
        .then(function (data) {
          rows = data.auctions || [];

          if (!rows.length) {
            root.remove();
            return;
          }

          render();
        })
        .catch(function () {
          list.textContent = '';
          list.appendChild(el('li', 'text-muted fs-7', 'Auction details are unavailable right now.'));
        });
    }

    load().then(function () {
      if (!root.isConnected) return;
      poll(load, REFRESH_MS);
      setInterval(function () {
        if (!document.hidden && rows.length) render();
      }, TICK_MS);
    });
  }

  /* Auction index — theme product cards via ?view=card */

  var cardCache = {};
  var handleCache = {};

  function shopifyProductId(row) {
    if (!row.productId) return '';
    var match = String(row.productId).match(/(\d+)$/);
    return match ? match[1] : '';
  }

  function rowHandle(row) {
    if (row.productHandle) return row.productHandle;
    var url = row.productUrl || '';
    var match = url.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function handleFromSuggestProduct(product) {
    if (!product) return '';
    if (product.handle) return product.handle;
    var url = product.url || '';
    var match = url.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function suggestQueries(row) {
    var queries = [];
    var seen = {};

    function add(value) {
      if (!value || seen[value]) return;
      seen[value] = true;
      queries.push(value);
    }

    add(rowHandle(row));
    add(row.parentSku);
    add(row.auctionSku);

    if (row.parentSku) {
      var skuMatch = row.parentSku.match(/[A-Z]{2,}\d+-[A-Z]{2}\d+[A-Z]?$/i);
      if (skuMatch) add(skuMatch[0]);
    }

    if (row.productTitle) {
      var titleMatch = row.productTitle.match(/\(([^)]+)\)/);
      if (titleMatch) add(titleMatch[1].trim());
      add(row.productTitle.split(' - ').pop());
    }

    return queries;
  }

  function fetchSuggestHandle(query, targetId) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root + 'search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=last';

    return fetch(url)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var products = data.resources && data.resources.results && data.resources.results.products;
        if (!products || !products.length) return '';

        if (targetId) {
          for (var i = 0; i < products.length; i++) {
            if (String(products[i].id) === String(targetId)) {
              return handleFromSuggestProduct(products[i]);
            }
          }
          return '';
        }

        return handleFromSuggestProduct(products[0]);
      })
      .catch(function () { return ''; });
  }

  function resolveHandle(row) {
    var direct = rowHandle(row);
    if (direct) return Promise.resolve(direct);

    var cacheKey = row.productId || row.parentSku || row.auctionSku || row.productTitle;
    if (handleCache[cacheKey]) return Promise.resolve(handleCache[cacheKey]);

    var targetId = shopifyProductId(row);
    var queries = suggestQueries(row);

    function tryNext(index) {
      if (index >= queries.length) return Promise.resolve('');
      return fetchSuggestHandle(queries[index], targetId).then(function (handle) {
        if (handle) {
          handleCache[cacheKey] = handle;
          return handle;
        }
        return tryNext(index + 1);
      });
    }

    return tryNext(0);
  }

  function resolveRowPairs(rows) {
    var seen = {};
    return Promise.all(
      rows.map(function (row, index) {
        return resolveHandle(row).then(function (handle) {
          return { index: index, handle: handle, row: row };
        });
      })
    ).then(function (results) {
      var pairs = [];
      results.sort(function (a, b) { return a.index - b.index; });
      results.forEach(function (item) {
        if (!item.handle || seen[item.handle]) return;
        seen[item.handle] = true;
        pairs.push({ handle: item.handle, row: item.row });
      });
      return pairs;
    });
  }

  function applyAuctionOverlay(grid, pairs, currency) {
    var cols = grid.querySelectorAll(':scope > .col.product-box');
    pairs.forEach(function (pair, index) {
      var col = cols[index];
      if (!col || !pair.row) return;
      var price = priceParts(pair.row, currency);

      col.querySelectorAll('.price-box .old-price, .price-box .special-price, .price-box .separator').forEach(function (node) {
        node.classList.add('d-none');
      });
      col.querySelectorAll('.price-box .regular-price.from, .price-box .regular-price.to').forEach(function (node) {
        node.classList.add('d-none');
      });

      var upcoming = isUpcoming(pair.row);
      col.classList.toggle('tc-auction-upcoming', upcoming);

      // product-badge.liquid renders "Live on eBay" from the ebay.is_live
      // metafield, which now also covers scheduled lots — so without this the
      // card claims to be live directly above "Bidding opens...".
      var ribbon = col.querySelector('.badge-live-auction');
      if (ribbon) {
        ribbon.textContent = upcoming ? 'Auction soon' : 'Live on eBay';
        ribbon.classList.toggle('badge-upcoming-auction', upcoming);
      }

      var priceBox = col.querySelector('.price-box');
      if (priceBox) {
        var priceLabel = col.querySelector('.tc-auction-price-label');
        if (!priceLabel) {
          priceLabel = el('span', 'tc-auction-price-label d-block fs-7 text-uppercase text-muted text-center mb-0');
          priceBox.insertBefore(priceLabel, priceBox.firstChild);
        }
        // Set every pass, not just on creation, so a lot that goes live between
        // polls stops being labelled as an opening bid.
        priceLabel.textContent = upcoming ? 'Opening bid' : 'Current price';
      }

      var priceNode = col.querySelector('.price-box .regular-price:not(.from):not(.to) .price') ||
        col.querySelector('.price-box .price-item:not(.old-price):not(.special-price) .price');
      if (priceNode) priceNode.textContent = price.amount;

      var anchor = col.querySelector('.product-title-wrapper') || col.querySelector('.product-title');
      if (!anchor) return;

      var countdown = col.querySelector('.tc-auction-countdown');
      if (!countdown) {
        var wrap = el('div', 'tc-auction-countdown-wrap d-flex justify-content-center mb-0 mt-2');
        // Font size, inline padding and gap are set in custom.css instead of via
        // fs-6/px-3/gap-2: those utilities are !important, so they cannot be
        // scaled down on mobile, where a 2-up card is only ~165px wide and
        // "10 HOURS 43 MINUTES" overflowed and got clipped. Desktop is unchanged.
        countdown = el('span', 'tc-auction-countdown badge fw-semibold py-2 border d-inline-flex align-items-center');
        wrap.appendChild(countdown);
        anchor.parentNode.insertBefore(wrap, anchor);
      } else {
        countdown.classList.add('d-inline-flex', 'align-items-center');
      }

      // Toggled rather than set once: the badge element is reused across polls,
      // so a lot that opens mid-session has to shed its upcoming styling.
      var stream = isStreamLot(pair.row);
      countdown.classList.toggle('bg-light', !upcoming);
      countdown.classList.toggle('text-dark', !upcoming);
      countdown.classList.toggle('tc-auction-upcoming-badge', upcoming);

      // Hidden rather than repurposed on a stream lot: the card already carries
      // the "Live on eBay" ribbon and a Watch livestream button, so a third
      // marker in the same card was just repetition.
      var countdownWrap = countdown.closest('.tc-auction-countdown-wrap');
      if (countdownWrap) countdownWrap.classList.toggle('d-none', stream);

      // Any action row left over from a previous poll, before either branch
      // decides whether this card should have one.
      var staleActions = col.querySelector('.tc-auction-actions');
      if (staleActions) staleActions.remove();

      if (stream) {
        // Bidding has moved into the stream, so neither the price nor the
        // countdown says anything true — the buttons are the whole story.
        if (priceBox) priceBox.classList.add('d-none');
        countdown.removeAttribute('data-ends-at');

        var footer = col.querySelector('.product-footer');
        if (footer) footer.appendChild(streamActions(pair.row, 'mt-2 justify-content-center'));
        return;
      }

      if (priceBox) priceBox.classList.remove('d-none');

      if (upcoming) {
        // No data-ends-at, so the countdown ticker leaves this badge alone.
        countdown.removeAttribute('data-ends-at');
        countdown.replaceChildren();
        countdown.appendChild(el('span', null, openingText(pair.row)));
        return;
      }

      countdown.setAttribute('data-ends-at', pair.row.endsAt);
      fillCountdown(countdown, msUntil(pair.row.endsAt), false);
    });
  }

  function tickGridCountdowns(grid) {
    grid.querySelectorAll('.tc-auction-countdown[data-ends-at]').forEach(function (node) {
      fillCountdown(node, msUntil(node.getAttribute('data-ends-at')), false);
    });
  }

  function fetchProductCard(handle) {
    if (cardCache[handle]) return Promise.resolve(cardCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '?view=card')
      .then(function (res) {
        return res.status === 200 ? res.text() : '';
      })
      .then(function (html) {
        if (html) cardCache[handle] = html;
        return html;
      });
  }

  function initGridWidgets(grid) {
    if (window.initVariantChanger) window.initVariantChanger(grid);
    if (window.ReloadYoptoReviews) window.ReloadYoptoReviews();
    if (window.initTooltips) {
      window.initTooltips(grid.querySelectorAll('[data-bs-toggle="tooltip"]'));
    }
  }

  function initIndex(root) {
    var endpoint = root.dataset.endpoint;
    if (!endpoint) return;

    var currency = root.dataset.currency;
    var limit = root.dataset.limit || '250';
    var grid = root.querySelector('[data-auction-grid]');
    var filters = root.querySelector('[data-auction-filters]');
    var sidebar = root.querySelector('[data-auction-sidebar]');
    var content = root.querySelector('[data-auction-content]');
    var counter = root.querySelector('[data-auction-total]');
    var loading = root.querySelector('[data-auction-loading]');
    var empty = root.querySelector('[data-auction-empty]');

    var activeFiltersBox = root.querySelector('[data-auction-active-filters]');
    var activeList = root.querySelector('[data-auction-active-list]');
    var clearAllLink = root.querySelector('[data-auction-clear]');

    var all = [];
    var renderToken = 0;
    var builtVendors = '';
    var vendorList = [];

    // ?vendor=pokemon, or ?vendor=pokemon,yugioh for more than one.
    function vendorsFromUrl() {
      var raw = new URLSearchParams(window.location.search).get('vendor') || '';
      return raw
        .split(',')
        .map(vendorSlug)
        .filter(Boolean);
    }

    var activeVendors = vendorsFromUrl();

    var sortSelect = root.querySelector('[data-auction-sort]');
    var sortTitle = root.querySelector('[data-auction-sort-title]');
    var sortOptions = root.querySelector('[data-role="sort-options"]');
    var DEFAULT_SORT = 'ending-soonest';

    function sortFromUrl() {
      return new URLSearchParams(window.location.search).get('sort') || DEFAULT_SORT;
    }

    var activeSort = sortFromUrl();

    function isActive(vendor) {
      return activeVendors.indexOf(vendorSlug(vendor)) !== -1;
    }

    function endsAt(row) {
      var time = new Date(row.endsAt).getTime();
      return isNaN(time) ? Infinity : time;
    }

    function price(row) {
      return Number(Number(row.bidCount) > 0 ? row.currentBid : row.startPrice) || 0;
    }

    function comparatorFor(sort) {
      switch (sort) {
        case 'ending-latest':
          return function (a, b) { return endsAt(b) - endsAt(a); };
        case 'price-ascending':
          return function (a, b) { return price(a) - price(b); };
        case 'price-descending':
          return function (a, b) { return price(b) - price(a); };
        case 'bids-descending':
          return function (a, b) { return (Number(b.bidCount) || 0) - (Number(a.bidCount) || 0); };
        default:
          // What the API already returns, but re-applied so a poll cannot reorder
          // the grid out from under a chosen sort.
          return function (a, b) { return endsAt(a) - endsAt(b); };
      }
    }

    function sortRows(rows) {
      var within = comparatorFor(activeSort);
      // State always wins: live lots stay above upcoming ones whichever sort is
      // chosen, because they are the only ones anyone can bid on today.
      return rows.slice().sort(function (a, b) {
        var byState = statePriority(a) - statePriority(b);
        return byState !== 0 ? byState : within(a, b);
      });
    }

    function visibleRows() {
      var rows = activeVendors.length
        ? all.filter(function (row) { return isActive(row.vendor); })
        : all;
      return sortRows(rows);
    }

    // Keep the address bar in step so a filtered view can be linked or bookmarked.
    function syncUrl(push) {
      var url = new URL(window.location.href);
      if (activeVendors.length) {
        url.searchParams.set('vendor', activeVendors.join(','));
      } else {
        url.searchParams.delete('vendor');
      }
      if (activeSort && activeSort !== DEFAULT_SORT) {
        url.searchParams.set('sort', activeSort);
      } else {
        url.searchParams.delete('sort');
      }
      if (url.href === window.location.href) return;
      window.history[push ? 'pushState' : 'replaceState'](
        { auctionVendors: activeVendors, auctionSort: activeSort },
        '',
        url.href
      );
    }

    // Repaints the trigger, the tick in the list and the hidden select to match
    // activeSort. main.js only writes the select value, so all of this is ours.
    function paintSort() {
      if (sortSelect) sortSelect.value = activeSort;

      if (sortTitle) {
        var chosen = sortOptions && sortOptions.querySelector('li[data-value="' + activeSort + '"]');
        var caret = sortTitle.querySelector('svg');
        sortTitle.textContent = chosen ? chosen.getAttribute('data-name') : activeSort;
        if (caret) sortTitle.appendChild(caret);
      }

      if (sortOptions) {
        Array.prototype.forEach.call(sortOptions.querySelectorAll('li'), function (item) {
          item.classList.toggle('selected', item.getAttribute('data-value') === activeSort);
        });
      }
    }

    function applySort(value) {
      var next = value || DEFAULT_SORT;
      if (!sortOptions || !sortOptions.querySelector('li[data-value="' + next + '"]')) {
        next = DEFAULT_SORT; // an unrecognised ?sort= falls back rather than emptying the grid
      }
      if (next === activeSort) return false;
      activeSort = next;
      paintSort();
      return true;
    }

    if (sortOptions) {
      applySort(activeSort);
      paintSort();

      // Bound on the options rather than the select: main.js sets the value with
      // jQuery's .trigger('change'), which never reaches an addEventListener.
      sortOptions.addEventListener('click', function (event) {
        var item = event.target.closest('li[data-value]');
        if (!item) return;

        var changed = applySort(item.getAttribute('data-value'));
        // The document-level close handler ignores clicks inside the dropdown,
        // so it has to be dismissed here.
        var select = root.querySelector('[data-role="sort"]');
        if (select) select.classList.remove('active');
        if (!changed) return;

        syncUrl(true);
        render();
      });
    }

    function render() {
      renderActiveFilters();
      var rows = visibleRows();
      var token = ++renderToken;

      return resolveRowPairs(rows).then(function (pairs) {
        if (token !== renderToken) return;

        if (!pairs.length) {
          grid.innerHTML = '';
          grid.hidden = true;
          if (empty) empty.hidden = rows.length === 0;
          if (counter) {
            counter.textContent = countText(rows);
            counter.hidden = rows.length === 0;
          }
          return;
        }

        return Promise.all(
          pairs.map(function (pair) {
            return fetchProductCard(pair.handle).then(function (html) {
              return { html: html, row: pair.row };
            });
          })
        ).then(function (items) {
          if (token !== renderToken) return;
          var rendered = items.filter(function (item) { return item.html; });
          grid.innerHTML = rendered.map(function (item) { return item.html; }).join('');
          grid.hidden = rendered.length === 0;
          if (empty) empty.hidden = rendered.length !== 0;
          if (counter) {
            counter.textContent = countText(rows);
            counter.hidden = rendered.length === 0;
          }
          if (rendered.length) {
            applyAuctionOverlay(grid, rendered, currency);
            initGridWidgets(grid);
          }
        });
      });
    }

    function vendorCount(vendor) {
      var slug = vendorSlug(vendor);
      var total = 0;
      all.forEach(function (row) {
        if (vendorSlug(row.vendor) === slug) total += 1;
      });
      return total;
    }

    // Same markup a Shopify list facet renders in main-collection-product-grid,
    // so collection-product.css styles it without a single extra rule.
    function filterItem(vendor, index) {
      var id = 'Filter-Auction-Brand-' + index;

      var input = document.createElement('input');
      input.type = 'checkbox';
      input.name = 'filter.p.vendor';
      input.value = vendor;
      input.id = id;
      input.autocomplete = 'off';
      input.setAttribute('aria-label', 'filter');
      input.checked = isActive(vendor);
      input.addEventListener('change', onFilterChange);

      var label = el('label', 'swatches-hover w-100 d-flex justify-content-start align-items-center facet-checkbox');
      label.htmlFor = id;
      label.appendChild(input);
      label.appendChild(el('span', 'radio-label'));
      label.appendChild(el('span', 'filter-label me-auto', vendor));
      label.appendChild(el('span', 'count float-end', '(' + vendorCount(vendor) + ')'));

      var item = document.createElement('li');
      item.appendChild(label);
      return item;
    }

    function onFilterChange() {
      setVendors(
        Array.prototype.filter
          .call(filters.querySelectorAll('input[type="checkbox"]'), function (input) {
            return input.checked;
          })
          .map(function (input) {
            return vendorSlug(input.value);
          })
      );
    }

    // Single path for every way a vendor can be added or dropped: the sidebar
    // checkboxes, a chip's x, and Clear all.
    function setVendors(next) {
      activeVendors = next;
      if (filters) {
        Array.prototype.forEach.call(filters.querySelectorAll('input[type="checkbox"]'), function (input) {
          input.checked = isActive(input.value);
        });
      }
      syncUrl(true);
      render();
    }

    function vendorNameFor(slug) {
      for (var i = 0; i < vendorList.length; i += 1) {
        if (vendorSlug(vendorList[i]) === slug) return vendorList[i];
      }
      return slug; // a hand-written ?vendor= with nothing live still gets a chip to remove
    }

    function urlForVendors(list) {
      var url = new URL(window.location.href);
      if (list.length) {
        url.searchParams.set('vendor', list.join(','));
      } else {
        url.searchParams.delete('vendor');
      }
      return url.pathname + url.search;
    }

    function without(slug) {
      return activeVendors.filter(function (item) { return item !== slug; });
    }

    function renderActiveFilters() {
      if (!activeFiltersBox || !activeList) return;

      activeFiltersBox.hidden = activeVendors.length === 0;
      activeList.textContent = '';

      activeVendors.forEach(function (slug) {
        var name = vendorNameFor(slug);
        var remaining = without(slug);

        var link = el('a', null, name);
        link.href = urlForVendors(remaining);
        link.setAttribute('aria-label', 'Remove ' + name);
        link.addEventListener('click', function (event) {
          event.preventDefault();
          setVendors(remaining);
        });

        var item = document.createElement('li');
        item.appendChild(link);
        activeList.appendChild(item);
      });

      if (clearAllLink) clearAllLink.href = urlForVendors([]);
    }

    if (clearAllLink) {
      clearAllLink.addEventListener('click', function (event) {
        event.preventDefault();
        setVendors([]);
      });
    }

    function buildFilters(vendors) {
      vendorList = vendors; // chip labels resolve their display name from this
      if (!filters) return;

      // Only rebuild when the vendor set itself changes, so a poll landing while
      // someone is picking boxes cannot wipe their selection.
      var signature = vendors.join('|');
      if (signature === builtVendors) {
        updateFilterCounts();
        return;
      }
      builtVendors = signature;

      filters.textContent = '';
      vendors.forEach(function (vendor, index) {
        filters.appendChild(filterItem(vendor, index + 1));
      });

      // With no vendors to filter on there is nothing to put in the sidebar, so
      // hand its width back to the grid.
      var hasVendors = vendors.length > 0;
      if (sidebar) sidebar.hidden = !hasVendors;
      if (content) {
        if (!content.dataset.defaultClass) content.dataset.defaultClass = content.className;
        content.className = hasVendors ? content.dataset.defaultClass : 'col-12';
      }
      var filterBtn = root.querySelector('.filter-btn');
      if (filterBtn) filterBtn.hidden = !hasVendors;
    }

    function updateFilterCounts() {
      if (!filters) return;
      Array.prototype.forEach.call(filters.querySelectorAll('li'), function (item) {
        var input = item.querySelector('input[type="checkbox"]');
        var count = item.querySelector('.count');
        if (input && count) count.textContent = '(' + vendorCount(input.value) + ')';
      });
    }

    // Back/forward between filtered views, and links landing with ?vendor= set.
    window.addEventListener('popstate', function () {
      activeVendors = vendorsFromUrl();
      if (filters) {
        Array.prototype.forEach.call(filters.querySelectorAll('input[type="checkbox"]'), function (input) {
          input.checked = isActive(input.value);
        });
      }
      applySort(sortFromUrl());
      render();
    });

    function load() {
      return request(endpoint, 'limit=' + encodeURIComponent(limit))
        .then(function (data) {
          all = data.auctions || [];

          buildFilters((data.vendors || []).filter(Boolean).sort());
          // Normalises a hand-written ?vendor=Yu-Gi-Oh! to its slug. A vendor with
          // nothing live is left in place on purpose, so the page says "no auctions
          // running" rather than silently showing every other game instead.
          syncUrl(false);
          return render();
        })
        .catch(function () {
          if (loading) loading.textContent = 'Auctions are unavailable right now.';
        })
        .then(function () {
          if (loading && loading.parentNode) loading.remove();
        });
    }

    load().then(function () {
      poll(load, REFRESH_MS);
      setInterval(function () {
        if (!document.hidden) tickGridCountdowns(grid);
      }, TICK_MS);
    });
  }

  function boot() {
    if (window.__tcAuctionBoot) return;
    window.__tcAuctionBoot = true;

    Array.prototype.forEach.call(document.querySelectorAll('[data-tc-auction-panel]'), initPanel);
    Array.prototype.forEach.call(document.querySelectorAll('[data-tc-auction-index]'), initIndex);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
